import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type { BatteryProposalDto } from "../dto/battery-proposal.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

export const INITIAL_SETUP_NOT_RECORDED = `The server did not record initial setup as complete.`

/**
 * Records guided initial setup complete for a Battery-Only Proposal. The server
 * decides whether completion is permitted, so the returned proposal is the
 * authority on whether setup is finished.
 */
export default function useCompleteBatteryInitialSetup(): UseMutationResult<
  BatteryProposalDto,
  unknown,
  { proposalId: string }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ proposalId }) => {
      const proposal =
        await batteryProposalService.completeInitialSetup(proposalId)

      // A 200 whose body still has no timestamp is not a completion. Failing
      // here keeps it out of the cache, where it would silently leave the
      // seller on a setup page that reports no error.
      if (proposal.initialSetupCompletedAt == null) {
        throw new Error(INITIAL_SETUP_NOT_RECORDED)
      }

      return proposal
    },
    onSuccess: async (proposal, { proposalId }) => {
      // The pricing POST that ran a moment ago invalidated this same key, so an
      // in-flight refetch started before the server recorded completion. Left
      // alone it resolves last and writes the pre-completion proposal back over
      // this one, bouncing the seller into setup with nothing to show for it.
      await queryClient.cancelQueries({
        queryKey: queryKeys.batteryProposal.byId(proposalId),
      })
      queryClient.setQueryData(
        queryKeys.batteryProposal.byId(proposalId),
        proposal,
      )
    },
  })
}
